// src/hooks/useAutoRefresh.js
// Calls the provided `refreshFn` immediately on mount, then every `intervalMs`.
// Returns `lastUpdated` — a Date object updated after each successful call.
// The interval is cleared automatically when the component unmounts.

import { useState, useEffect, useRef } from 'react';

export function useAutoRefresh(refreshFn, intervalMs = 30000) {
  const [lastUpdated, setLastUpdated] = useState(null);
  const fnRef = useRef(refreshFn);

  // Keep the ref current so we always call the latest version of the function
  useEffect(() => { fnRef.current = refreshFn; }, [refreshFn]);

  useEffect(() => {
    let cancelled = false;
    let running = false;

    async function run() {
      if (running) return; // never pile a new refresh on top of one still running
      running = true;
      try {
        await fnRef.current();
        if (!cancelled) setLastUpdated(new Date());
      } finally { running = false; }
    }

    run(); // immediate first call
    const id = setInterval(() => { if (!document.hidden) run(); }, intervalMs); // quiet while the tab is hidden
    const onVisible = () => { if (!document.hidden) run(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { cancelled = true; clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, [intervalMs]); // eslint-disable-line

  return lastUpdated;
}
